import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';

const HistorialAcademicoEstudianteWidget = () => {
  const { user, API } = useContext(AuthContext);
  const [semestres, setSemestres] = useState([]);
  const [cursoAcademico, setCursoAcademico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSemestre, setSelectedSemestre] = useState(null);

  useEffect(() => {
    loadHistorial();
  }, []);

  const loadHistorial = async () => {
    try {
      const response = await axios.get(`${API}/estudiantes/mi-historial-academico`);
      const lista = response.data?.semestres || [];
      setSemestres(lista);
      setCursoAcademico(response.data?.curso_academico || null);
      if (lista.length > 0) {
        setSelectedSemestre(lista[lista.length - 1].numero);
      }
    } catch (error) {
      console.error('Error cargando historial académico:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="shadow-lg border-0" data-testid="historial-academico-loading">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (semestres.length === 0) {
    return (
      <Card className="shadow-lg border-0" data-testid="historial-academico-empty">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center text-xl">
            <i className="fas fa-graduation-cap text-purple-600 mr-3"></i>
            Mi Historial Académico
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <i className="fas fa-book-open text-gray-300 text-4xl mb-4"></i>
            <p className="text-gray-500">Todavía no tienes clases registradas por semestre.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const semestreActivo = semestres.find(s => s.numero === selectedSemestre) || semestres[semestres.length - 1];

  const renderEstadoBadge = (clase) => {
    if (clase.estado === 'aprobada') {
      return (
        <Badge className="bg-green-100 text-green-800 border border-green-300 text-xs font-semibold">
          <i className="fas fa-check-circle mr-1"></i>
          Aprobada · {clase.promedio}
        </Badge>
      );
    }
    if (clase.estado === 'no_aprobada') {
      return (
        <Badge className="bg-red-100 text-red-800 border border-red-300 text-xs font-semibold">
          <i className="fas fa-times-circle mr-1"></i>
          No aprobada · {clase.promedio}
        </Badge>
      );
    }
    return (
      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs font-semibold">
        <i className="fas fa-hourglass-half mr-1"></i>
        Pendiente
      </Badge>
    );
  };

  const getSemestreCardClasses = (sem, isSelected) => {
    if (isSelected) {
      return 'border-purple-500 bg-purple-50 ring-2 ring-purple-200';
    }
    return 'border-gray-200 hover:border-purple-300 hover:bg-gray-50';
  };

  return (
    <Card className="shadow-lg border-0" data-testid="historial-academico-widget">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center text-xl">
          <i className="fas fa-graduation-cap text-purple-600 mr-3"></i>
          Mi Historial Académico por Semestre
        </CardTitle>
        <p className="text-xs text-gray-500 mt-1">
          Aprobada ≥ 70 · No aprobada &lt; 70 · Pendiente sin calificar
        </p>
      </CardHeader>
      <CardContent>
        {/* 📊 Progreso del Curso Académico (Semestres 1..4) */}
        {cursoAcademico && cursoAcademico.total_clases > 0 && (
          <div
            className="mb-6 rounded-xl border border-purple-200 bg-gradient-to-br from-purple-50 via-white to-blue-50 p-5 shadow-sm"
            data-testid="progreso-curso-academico"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3">
              <div>
                <h4 className="text-lg font-bold text-gray-900 flex items-center">
                  <i className="fas fa-route text-purple-600 mr-2"></i>
                  Progreso del Curso (4 Semestres)
                </h4>
                <p className="text-xs text-gray-500 mt-0.5">
                  Cuánto avanzaste y cuánto te falta para completar todo el plan de estudios.
                </p>
              </div>
              <div className="text-right">
                <div className="text-3xl font-extrabold text-purple-700 leading-none" data-testid="progreso-curso-porcentaje">
                  {cursoAcademico.porcentaje_cursado ?? cursoAcademico.porcentaje_completado}%
                </div>
                <div className="text-xs text-gray-500 mt-1">cursado</div>
              </div>
            </div>

            {/* Barra de progreso segmentada por semestre (1..4) */}
            {(() => {
              const SEM_COLORS = {
                1: 'bg-blue-500',
                2: 'bg-emerald-500',
                3: 'bg-purple-500',
                4: 'bg-amber-500',
              };
              const detalle = cursoAcademico.detalle_semestres || [];
              return (
                <>
                  <div className="flex w-full h-5 rounded-full overflow-hidden mb-2 border border-gray-200 bg-gray-100" data-testid="progreso-curso-bar-segmentada">
                    {detalle.map((sem, idx) => {
                      const color = SEM_COLORS[sem.numero] || 'bg-gray-400';
                      // Coloreado 100% si el estudiante tuvo cualquier clase inscrita en ese semestre
                      // (aprobada, no aprobada o en curso). Gris solo si nunca estudió ese semestre.
                      const estudiado = (sem.total || 0) > 0;
                      const pct = estudiado ? 100 : 0;
                      const isFirst = idx === 0;
                      const isLast = idx === detalle.length - 1;
                      return (
                        <div
                          key={sem.numero}
                          className={`flex-1 relative bg-gray-200 ${!isLast ? 'border-r border-white' : ''}`}
                          title={`${sem.nombre}: ${sem.total} clase(s) inscrita(s) · ${sem.aprobadas} aprobadas · ${sem.no_aprobadas} no aprobadas · ${sem.pendientes} en curso`}
                          data-testid={`progreso-segmento-${sem.numero}`}
                        >
                          <div
                            className={`h-full ${color} transition-all duration-500 ${isFirst ? 'rounded-l-full' : ''} ${isLast && pct >= 100 ? 'rounded-r-full' : ''}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      );
                    })}
                  </div>

                  {/* Leyenda de semestres */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                    {detalle.map((sem) => {
                      const color = SEM_COLORS[sem.numero] || 'bg-gray-400';
                      const estudiado = (sem.total || 0) > 0;
                      const cursadas = sem.cursadas || 0;
                      const completo = estudiado && cursadas === sem.total;
                      return (
                        <div
                          key={sem.numero}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs border ${
                            estudiado ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-70'
                          }`}
                        >
                          <span className={`w-3 h-3 rounded-full ${estudiado ? color : 'bg-gray-300'} flex-shrink-0`}></span>
                          <span className="flex-1 min-w-0">
                            <span className="font-semibold text-gray-800 truncate block">
                              Sem. {sem.numero}
                            </span>
                            <span className="text-gray-500">
                              {estudiado
                                ? `${cursadas}/${sem.total} cursadas${completo ? ' ✓' : ''}`
                                : 'Sin estudiar'}
                            </span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
              <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                <span className="text-green-700 font-semibold">
                  <i className="fas fa-check-circle mr-1"></i>Aprobadas
                </span>
                <span className="font-bold text-green-800" data-testid="progreso-aprobadas">
                  {cursoAcademico.aprobadas}
                </span>
              </div>
              <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                <span className="text-red-700 font-semibold">
                  <i className="fas fa-times-circle mr-1"></i>No aprobadas
                </span>
                <span className="font-bold text-red-800" data-testid="progreso-no-aprobadas">
                  {cursoAcademico.no_aprobadas}
                </span>
              </div>
              <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <span className="text-amber-700 font-semibold">
                  <i className="fas fa-hourglass-half mr-1"></i>En curso
                </span>
                <span className="font-bold text-amber-800" data-testid="progreso-en-curso">
                  {cursoAcademico.pendientes_inscritas}
                </span>
              </div>
              {cursoAcademico.faltan_inscribir > 0 && (
                <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                  <span className="text-blue-700 font-semibold">
                    <i className="fas fa-flag-checkered mr-1"></i>Te faltan
                  </span>
                  <span className="font-bold text-blue-800" data-testid="progreso-faltan">
                    {cursoAcademico.faltan_inscribir}
                  </span>
                </div>
              )}
            </div>

            <div className="mt-3 text-center text-xs text-gray-600">
              <span className="font-semibold text-gray-800">{cursoAcademico.cursadas ?? cursoAcademico.aprobadas}</span>
              {' de '}
              <span className="font-semibold text-gray-800">{cursoAcademico.total_clases}</span>
              {' clases cursadas del plan de estudios'}
              {cursoAcademico.aprobadas > 0 && (
                <>
                  {' · '}
                  <span className="text-green-700 font-semibold">{cursoAcademico.aprobadas} aprobadas</span>
                </>
              )}
              {cursoAcademico.pendientes_inscritas > 0 && (
                <>
                  {' · '}
                  <span className="text-amber-700">{cursoAcademico.pendientes_inscritas} en curso</span>
                </>
              )}
            </div>
          </div>
        )}

        {/* Tarjetas de semestres (orden cronológico) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
          {semestres.map((sem) => {
            const isSelected = sem.numero === selectedSemestre;
            const r = sem.resumen || { total: 0, aprobadas: 0, no_aprobadas: 0, pendientes: 0 };
            return (
              <button
                key={sem.numero}
                type="button"
                onClick={() => setSelectedSemestre(sem.numero)}
                className={`text-left p-4 rounded-lg border-2 cursor-pointer transition-all ${getSemestreCardClasses(sem, isSelected)}`}
                data-testid={`historial-semestre-${sem.numero}`}
              >
                <div className="text-xs text-gray-500 mb-1">{sem.ano}</div>
                <h3 className="font-semibold text-gray-900 mb-2 text-sm">
                  {sem.nombre}
                </h3>
                <div className="text-2xl font-bold text-purple-600 mb-2">
                  {r.total}
                  <span className="text-xs font-medium text-gray-500 ml-1">clases</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap text-xs">
                  {r.aprobadas > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-semibold">
                      <i className="fas fa-check"></i>{r.aprobadas}
                    </span>
                  )}
                  {r.no_aprobadas > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold">
                      <i className="fas fa-times"></i>{r.no_aprobadas}
                    </span>
                  )}
                  {r.pendientes > 0 && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold">
                      <i className="fas fa-clock"></i>{r.pendientes}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Detalle del semestre seleccionado */}
        {semestreActivo && (
          <div data-testid="historial-detalle-semestre">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <i className="fas fa-list mr-2 text-purple-600"></i>
              Clases de {semestreActivo.nombre}
              <span className="text-xs text-gray-500 ml-2">({semestreActivo.ano})</span>
            </h4>

            {semestreActivo.clases.length === 0 ? (
              <div className="text-center py-6 text-gray-500 text-sm">
                No hay clases registradas en este semestre.
              </div>
            ) : (
              <div className="space-y-2">
                {semestreActivo.clases.map((clase) => (
                  <div
                    key={clase.id}
                    className={`flex flex-col md:flex-row md:items-center md:justify-between gap-2 p-3 rounded-lg border ${
                      clase.estado === 'aprobada'
                        ? 'bg-green-50 border-green-200'
                        : clase.estado === 'no_aprobada'
                        ? 'bg-red-50 border-red-200'
                        : 'bg-amber-50 border-amber-200'
                    }`}
                    data-testid={`historial-clase-${clase.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="font-medium text-gray-900 text-sm truncate">
                          {clase.titulo}
                        </h5>
                        {!clase.activa && (
                          <Badge variant="outline" className="text-[10px] bg-gray-100 text-gray-600 border-gray-300">
                            Histórica
                          </Badge>
                        )}
                      </div>
                      {clase.profesor_nombre && (
                        <p className="text-xs text-gray-600 mt-1">
                          <i className="fas fa-user-tie mr-1"></i>
                          {clase.profesor_nombre}
                        </p>
                      )}
                    </div>
                    <div>{renderEstadoBadge(clase)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default HistorialAcademicoEstudianteWidget;
